import { useState, useMemo, useCallback, useEffect } from "react";
import { Plus, Loader2, ArrowDownAZ, Copy, Download } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EquipmentGroupCard } from "./equipment/EquipmentGroupCard";
import { CarnetAtaEquipmentTable } from "./equipment/CarnetAtaEquipmentTable";
import {
  useProjectEquipmentGroups,
  useCreateEquipmentGroup,
  useCopyEquipmentPlanning,
  useUpdateEquipmentGroup,
  useDeleteEquipmentGroup,
  useCreateTemporaryEquipment,
  useUpdateTemporaryEquipment,
  useCreateEquipmentAssignment,
  useUpdateEquipmentAssignment,
  useDeleteEquipmentAssignment,
  useAssignInstance,
  useUnassignInstance,
} from "@/hooks/useProjectEquipment";
import { useUpdateEquipment } from "@/hooks/useEquipment";
import { useEquipmentConflicts } from "@/hooks/useEquipmentConflicts";
import { useBatchEquipmentInstances } from "@/hooks/useEquipmentInstances";
import { useCameraPlans, useCamerasForProject } from "@/hooks/useCameraPlans";
import type { CameraWithPlan } from "@/hooks/useCameraPlans";
import type { ProjectWithRelations } from "@/hooks/useProjects";
import { downloadCarnetAtaCsv } from "@/lib/carnet-ata-export";
import { generateUUID } from "@/lib/utils";
import { CopyFromProjectSelector } from "./CopyFromProjectSelector";
import { toast } from "sonner";

type StateView = "packing" | "rigging" | "carnet_ata";

interface ProjectEquipmentTabProps {
  project: ProjectWithRelations;
}

export function ProjectEquipmentTab({ project }: ProjectEquipmentTabProps) {
  const stateViewKey = `equipment-state-view-${project.id}`;
  const [stateView, setStateView] = useState<StateView>(() => {
    try {
      const stored = localStorage.getItem(stateViewKey);
      if (
        stored === "packing" ||
        stored === "rigging" ||
        stored === "carnet_ata"
      )
        return stored;
    } catch {
      // ignore
    }
    return "packing";
  });
  const handleStateViewChange = (v: string) => {
    const value = v as StateView;
    setStateView(value);
    try {
      localStorage.setItem(stateViewKey, value);
    } catch {
      // ignore
    }
  };
  const [isAddGroupDialogOpen, setIsAddGroupDialogOpen] = useState(false);
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [selectedExportGroupIds, setSelectedExportGroupIds] = useState<
    string[]
  >([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newAssignmentId, setNewAssignmentId] = useState<string | null>(null);
  const [sourceProjectId, setSourceProjectId] = useState<string | null>(
    project.previous_edition_project_id ?? null,
  );
  const [mapCameraAssignments, setMapCameraAssignments] = useState(true);

  // Camera plan selection
  const storageKey = `equipment-camera-plan-${project.id}`;
  const [selectedCameraPlanId, setSelectedCameraPlanId] = useState<
    string | null
  >(() => {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const { data: cameraPlans = [] } = useCameraPlans(project.id);
  const { data: allCameras = [] } = useCamerasForProject(project.id);

  // Auto-select first plan if none selected or selected plan no longer exists
  useEffect(() => {
    if (cameraPlans.length === 0) return;
    if (
      !selectedCameraPlanId ||
      !cameraPlans.some((p) => p.id === selectedCameraPlanId)
    ) {
      setSelectedCameraPlanId(cameraPlans[0].id);
    }
  }, [cameraPlans, selectedCameraPlanId]);

  // Persist selection
  useEffect(() => {
    try {
      if (selectedCameraPlanId) {
        localStorage.setItem(storageKey, selectedCameraPlanId);
      }
    } catch {
      // ignore
    }
  }, [selectedCameraPlanId, storageKey]);

  // Group cameras by equipmentGroupID for the selected plan
  const camerasByGroupId = useMemo(() => {
    const map: Record<string, CameraWithPlan[]> = {};
    for (const cam of allCameras) {
      if (
        cam.equipmentGroupID &&
        (!selectedCameraPlanId || cam.cameraPlanID === selectedCameraPlanId)
      ) {
        if (!map[cam.equipmentGroupID]) {
          map[cam.equipmentGroupID] = [];
        }
        map[cam.equipmentGroupID].push(cam);
      }
    }
    return map;
  }, [allCameras, selectedCameraPlanId]);

  const { data: groups = [], isLoading } = useProjectEquipmentGroups(
    project.id,
  );

  useEffect(() => {
    setSelectedExportGroupIds((current) => {
      const groupIds = groups.map((group) => group.id);
      const next = current.filter((id) => groupIds.includes(id));
      return next.length === current.length ? current : next;
    });
  }, [groups]);

  const { data: sourceGroups = [], isLoading: sourceGroupsLoading } =
    useProjectEquipmentGroups(sourceProjectId ?? undefined);
  const createGroup = useCreateEquipmentGroup();
  const copyEquipmentPlanning = useCopyEquipmentPlanning();
  const updateGroup = useUpdateEquipmentGroup();
  const deleteGroup = useDeleteEquipmentGroup();
  const createTemporary = useCreateTemporaryEquipment();
  const updateTemporary = useUpdateTemporaryEquipment();
  const createAssignment = useCreateEquipmentAssignment();
  const updateAssignment = useUpdateEquipmentAssignment();
  const updateEquipment = useUpdateEquipment();
  const deleteAssignment = useDeleteEquipmentAssignment();
  const assignInstance = useAssignInstance();
  const unassignInstance = useUnassignInstance();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
  const groupIds = useMemo(() => groups.map((g) => g.id), [groups]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = groups.findIndex((g) => g.id === active.id);
      const newIndex = groups.findIndex((g) => g.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...groups];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Persist changed display_orders
      reordered.forEach((g, i) => {
        if (g.display_order !== i) {
          updateGroup.mutate({
            id: g.id,
            projectId: project.id,
            updates: { display_order: i },
          });
        }
      });
    },
    [groups, updateGroup, project.id],
  );

  const handleAutoSort = useCallback(() => {
    const sorted = [...groups].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );

    sorted.forEach((g, i) => {
      if (g.display_order !== i) {
        updateGroup.mutate({
          id: g.id,
          projectId: project.id,
          updates: { display_order: i },
        });
      }
    });
  }, [groups, updateGroup, project.id]);

  const handleExportCarnetAta = useCallback(() => {
    downloadCarnetAtaCsv(project, groups, selectedExportGroupIds);
    setIsExportDialogOpen(false);
  }, [groups, project, selectedExportGroupIds]);

  // Collect all equipment IDs and serialized equipment IDs
  const { equipmentIds, serializedEquipmentIds } = useMemo(() => {
    const ids = new Set<string>();
    const serializedIds = new Set<string>();
    for (const group of groups) {
      for (const assignment of group.assignments) {
        if (assignment.equipment_id) {
          ids.add(assignment.equipment_id);
          if (assignment.equipment?.is_serialized) {
            serializedIds.add(assignment.equipment_id);
          }
        }
      }
    }
    return {
      equipmentIds: Array.from(ids),
      serializedEquipmentIds: Array.from(serializedIds),
    };
  }, [groups]);

  // Batch fetch instances for all serialized equipment
  const { data: instancesByEquipmentId } = useBatchEquipmentInstances(
    serializedEquipmentIds,
  );

  // Fetch conflicts for all equipment in overlapping projects
  const { data: conflicts } = useEquipmentConflicts({
    projectId: project.id,
    startDate: project.planning_start_date || null,
    endDate: project.planning_end_date || null,
    equipmentIds,
  });

  const handleCreateGroup = () => {
    if (newGroupName.trim()) {
      // Close dialog immediately — the optimistic update renders the new
      // group instantly and any server failure surfaces as a toast + rollback.
      createGroup.mutate({
        project_id: project.id,
        name: newGroupName.trim(),
        display_order: groups.length,
        optimisticId: generateUUID(),
      });
      setNewGroupName("");
      setIsAddGroupDialogOpen(false);
    }
  };

  const handleCreateTemporary = async (name: string): Promise<string> => {
    const result = await createTemporary.mutateAsync({
      project_id: project.id,
      name,
    });
    return result.id;
  };

  const openCopyDialog = () => {
    setSourceProjectId(project.previous_edition_project_id ?? null);
    setMapCameraAssignments(true);
    setIsCopyDialogOpen(true);
  };

  const handleCopyEquipmentPlanning = async () => {
    if (!sourceProjectId || sourceGroups.length === 0) return;

    try {
      await copyEquipmentPlanning.mutateAsync({
        targetProjectId: project.id,
        sourceProjectId,
        mapCameraAssignments,
      });
      setIsCopyDialogOpen(false);
      toast.success("Equipment planning copied");
    } catch (error) {
      toast.error(
        `Failed to copy equipment planning: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  const handleAddEquipment = async (groupId: string) => {
    // Let Zero own the optimistic row. Only keep local UI intent in this tab.
    const id = generateUUID();
    setNewAssignmentId(id);
    try {
      await createAssignment.mutateAsync({
        group_id: groupId,
        amount: 1,
        projectId: project.id,
        optimisticId: id,
      });
    } catch (error) {
      setNewAssignmentId((current) => (current === id ? null : current));
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid gap-2 sm:flex sm:items-center sm:gap-4">
          <ToggleGroup
            type="single"
            value={stateView}
            onValueChange={(value) => {
              if (value) handleStateViewChange(value);
            }}
            variant="outline"
            size="sm"
            className="justify-start"
          >
            <ToggleGroupItem value="packing">Packing</ToggleGroupItem>
            <ToggleGroupItem value="rigging">Rigging</ToggleGroupItem>
            <ToggleGroupItem value="carnet_ata">Carnet ATA</ToggleGroupItem>
          </ToggleGroup>
          {stateView !== "carnet_ata" && cameraPlans.length > 0 && (
            <Select
              value={selectedCameraPlanId ?? ""}
              onValueChange={setSelectedCameraPlanId}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Camera Plan" />
              </SelectTrigger>
              <SelectContent>
                {cameraPlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:flex sm:items-center">
          <Button
            offlineEdit
            variant="outline"
            onClick={openCopyDialog}
            className="w-full sm:w-auto"
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy from
          </Button>
          <Button
            offlineEdit
            variant="outline"
            size="sm"
            onClick={handleAutoSort}
            disabled={groups.length < 2}
            className="w-full sm:w-auto"
          >
            <ArrowDownAZ className="h-4 w-4 mr-2" />
            Sort A–Z
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selectedExportGroupIds.length === 0) {
                setSelectedExportGroupIds(groups.map((group) => group.id));
              }
              setIsExportDialogOpen(true);
            }}
            disabled={groups.length === 0}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Export<span className="hidden sm:inline">&nbsp;ATA</span>
          </Button>
          <Button
            offlineEdit
            onClick={() => setIsAddGroupDialogOpen(true)}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add<span className="hidden sm:inline">&nbsp;Group</span>
          </Button>
        </div>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CardTitle className="text-lg mb-2">
              No equipment groups yet
            </CardTitle>
            <CardDescription>
              Create equipment groups to organize and track equipment for this
              project.
            </CardDescription>
            <div className="mt-4 flex flex-col items-center justify-center gap-2 sm:flex-row">
              <Button offlineEdit variant="outline" onClick={openCopyDialog}>
                <Copy className="h-4 w-4 mr-2" />
                Copy from
              </Button>
              <Button offlineEdit onClick={() => setIsAddGroupDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Group
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : stateView === "carnet_ata" ? (
        <CarnetAtaEquipmentTable
          groups={groups}
          onUpdateEquipment={(id, equipment) =>
            updateEquipment.mutate({ id, equipment })
          }
          onUpdateTemporaryEquipment={(id, updates) =>
            updateTemporary.mutate({ id, ...updates })
          }
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={groupIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {groups.map((group) => (
                <EquipmentGroupCard
                  key={group.id}
                  group={group}
                  stateView={stateView}
                  conflicts={conflicts}
                  instancesByEquipmentId={instancesByEquipmentId}
                  currentProjectId={project.id}
                  cameras={camerasByGroupId[group.id]}
                  newAssignmentId={newAssignmentId}
                  onNewAssignmentHandled={(assignmentId) => {
                    if (assignmentId === newAssignmentId) {
                      setNewAssignmentId(null);
                    }
                  }}
                  onUpdateGroup={(updates) =>
                    updateGroup.mutate({
                      id: group.id,
                      projectId: project.id,
                      updates,
                    })
                  }
                  onDeleteGroup={() =>
                    deleteGroup.mutate({ id: group.id, projectId: project.id })
                  }
                  onAddEquipment={handleAddEquipment}
                  onUpdateAssignment={(assignmentId, updates) =>
                    updateAssignment.mutate({
                      id: assignmentId,
                      projectId: project.id,
                      updates,
                    })
                  }
                  onDeleteAssignment={(assignmentId) =>
                    deleteAssignment.mutate({
                      id: assignmentId,
                      projectId: project.id,
                    })
                  }
                  onCreateTemporary={handleCreateTemporary}
                  onUpdateTemporary={(id, name) =>
                    updateTemporary.mutate({ id, name })
                  }
                  onAssignInstance={(assignmentId, instanceId) =>
                    assignInstance.mutate({
                      assignmentId,
                      instanceId,
                      projectId: project.id,
                    })
                  }
                  onUnassignInstance={(instanceAssignmentId) =>
                    unassignInstance.mutate({
                      id: instanceAssignmentId,
                      projectId: project.id,
                    })
                  }
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Dialog
        open={isAddGroupDialogOpen}
        onOpenChange={setIsAddGroupDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Equipment Group</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="groupName">Group Name</Label>
            <Input
              id="groupName"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="e.g., Audio, Lighting, Video..."
              onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddGroupDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleCreateGroup}
              disabled={!newGroupName.trim()}
            >
              Add Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Carnet ATA List</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <Label>Equipment Groups</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSelectedExportGroupIds(
                    selectedExportGroupIds.length === groups.length
                      ? []
                      : groups.map((group) => group.id),
                  )
                }
              >
                {selectedExportGroupIds.length === groups.length
                  ? "Clear"
                  : "Select All"}
              </Button>
            </div>
            <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border p-3">
              {groups.map((group) => {
                const checked = selectedExportGroupIds.includes(group.id);
                return (
                  <label
                    key={group.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-muted"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => {
                        setSelectedExportGroupIds((current) =>
                          value
                            ? [...new Set([...current, group.id])]
                            : current.filter((id) => id !== group.id),
                        );
                      }}
                    />
                    <span className="flex-1 text-sm font-medium">
                      {group.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {group.assignments.length}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsExportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportCarnetAta}
              disabled={selectedExportGroupIds.length === 0}
            >
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCopyDialogOpen} onOpenChange={setIsCopyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy from
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Project</Label>
              <CopyFromProjectSelector
                value={sourceProjectId}
                onChange={setSourceProjectId}
                currentProjectId={project.id}
                placeholder="Select source project"
                testId="copy-equipment-source-project"
              />
              {sourceProjectId && !sourceGroupsLoading ? (
                <p className="text-xs text-muted-foreground">
                  {sourceGroups.length} group
                  {sourceGroups.length === 1 ? "" : "s"} will be copied.
                </p>
              ) : null}
            </div>
            <label className="flex items-start gap-3 rounded-md border p-3 text-sm">
              <Checkbox
                checked={mapCameraAssignments}
                onCheckedChange={(checked) =>
                  setMapCameraAssignments(checked === true)
                }
              />
              <span className="space-y-1">
                <span className="block font-medium">
                  Map matching camera assignments
                </span>
                <span className="block text-muted-foreground">
                  Cameras are matched by shifted plan date or plan name, then by
                  camera number. Only unique matches are updated.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCopyDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              offlineEdit
              onClick={handleCopyEquipmentPlanning}
              disabled={
                !sourceProjectId ||
                sourceGroupsLoading ||
                sourceGroups.length === 0 ||
                copyEquipmentPlanning.isPending
              }
            >
              Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
